import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { adminRoutes } from "../../routers/AdminRouters";
import {
  useGetBillingDataQuery,
  useGetBillingDataUpcomingQuery,
  useGetLicensesQuery,
  useGetOrganisationScreensQuery,
  useGetSubscriptionsSummaryQuery,
  useGetProfileQuery,
} from "../../services";
import { selectOrganisationId } from "../../redux/organisationReducer";
import { selectSubscriptionSummary } from "../../redux/licenseReducer";
import { BillingRow } from "../../types";
import InvoiceModal from "../subscription/billings/InvoiceModal/InvoiceModal";

const Dashboard: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const themeBox = theme === "dark";

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  const organisationId = useAppSelector(selectOrganisationId);

  const [page, setPage] = React.useState(1);
  const [allBillingRows, setAllBillingRows] = React.useState<any[]>([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [licensePage, setLicensePage] = React.useState(1);
  const [allLicenses, setAllLicenses] = React.useState<any[]>([]);
  const [hasMoreLicenses, setHasMoreLicenses] = React.useState(true);
  const { data: billingData, isFetching } = useGetBillingDataQuery({
    page,
    limit: 10,
    organisation: organisationId,
  });

  const { data: licensesData, isFetching: isFetchingLicenses } =
    useGetLicensesQuery({
      organisationId,
      page: licensePage,
      limit: 50,
    });

  const { data: upcomingData } = useGetBillingDataUpcomingQuery({
    organisation: organisationId,
  });

  const { data: screensData } = useGetOrganisationScreensQuery(
    {
      organisationId,
      limit: -1,
    },
    { pollingInterval: 10000 },
  );

  const { data: summaryData, isLoading: summaryLoading, isFetching: summaryFetching, error: summaryError } = useGetSubscriptionsSummaryQuery({ organisationId: organisationId || "" }, { skip: !organisationId });

  const subscriptionSummary = useAppSelector(selectSubscriptionSummary);

  // Calculate License Stats
  const licenseResults = allLicenses;

  const assignedLicenses = licenseResults.filter((l: any) => l.isAssigned);
  const unassignedLicenses = licenseResults.filter((l: any) => !l.isAssigned);

  const formatCount = (count: number) => String(count).padStart(2, "0");

  const statAssignedPremium = formatCount(
    assignedLicenses.filter((l: any) => l.type === "PREMIUM").length,
  );
  const statAssignedBasic = formatCount(
    assignedLicenses.filter((l: any) => l.type === "BASIC").length,
  );
  const statUnassignedPremium = formatCount(
    unassignedLicenses.filter((l: any) => l.type === "PREMIUM").length,
  );
  const statUnassignedBasic = formatCount(
    unassignedLicenses.filter((l: any) => l.type === "BASIC").length,
  );
  const totalAssignedText = formatCount(assignedLicenses.length);
  const totalUnassignedText = formatCount(unassignedLicenses.length);

  // Calculate Screen Stats
  const screenResults = screensData?.results || [];

  // Total Screens: status === "Active"
  const totalScreens = screenResults.filter((s: any) => s.status === "Active");

  // Active Screens: streamingStatus in pending/streaming/paused
  const liveScreens = totalScreens.filter((s: any) =>
    ["WAITING_FOR_STREAM", "STREAMING", "PAUSED"].includes(s.streamingStatus),
  );

  // Idle Screens: streamingStatus === "IDLE"
  const idleScreens = totalScreens.filter(
    (s: any) => s.streamingStatus === "IDLE",
  );

  const statTotalScreensPremium = formatCount(
    totalScreens.filter((s: any) => s.type === "PREMIUM").length,
  );
  const statTotalScreensBasic = formatCount(
    totalScreens.filter((s: any) => s.type === "BASIC").length,
  );

  const statActiveScreensPremium = formatCount(
    liveScreens.filter((s: any) => s.type === "PREMIUM").length,
  );
  const statActiveScreensBasic = formatCount(
    liveScreens.filter((s: any) => s.type === "BASIC").length,
  );

  const statIdleScreensPremium = formatCount(
    idleScreens.filter((s: any) => s.type === "PREMIUM").length,
  );
  const statIdleScreensBasic = formatCount(
    idleScreens.filter((s: any) => s.type === "BASIC").length,
  );

  // Modal State
  const [openInvoice, setOpenInvoice] = React.useState(false);
  const [selectedInvoice, setSelectedInvoice] =
    React.useState<BillingRow | null>(null);

  const getProductStatsForModal = (
    products: any[],
    type: "PREMIUM" | "BASIC",
    variant: "LICENSE" | "ADDON_SCREEN",
  ) => {
    const filtered = (products || []).filter(
      (p: any) => p.type === type && p.variant === variant,
    );
    if (filtered.length === 0) return null;
    return filtered.reduce(
      (acc, p) => ({
        quantity: acc.quantity + (p.quantity || 0),
        price: p.price,
        totalPrice: acc.totalPrice + (p.totalPrice || 0),
      }),
      { quantity: 0, price: 0, totalPrice: 0 },
    );
  };

  const handleViewInvoice = (item: any) => {
    const products = item.products || [];
    const mapped: BillingRow = {
      id: item._id,
      billingDate: item.createdAt
        ? new Date(item.createdAt).toLocaleDateString()
        : "--",
      endDate: item.subscription?.endDate
        ? new Date(item.subscription.endDate).toLocaleDateString()
        : "--",
      invoiceId: item.code || "--",
      description: item.products?.[0]?.name || "Subscription Plan",
      premium: getProductStatsForModal(products, "PREMIUM", "LICENSE") || {
        quantity: 0,
        price: 0,
        totalPrice: 0,
      },
      normal: getProductStatsForModal(products, "BASIC", "LICENSE") || {
        quantity: 0,
        price: 0,
        totalPrice: 0,
      },
      premiumAddons: getProductStatsForModal(
        products,
        "PREMIUM",
        "ADDON_SCREEN",
      ) || { quantity: 0, price: 0, totalPrice: 0 },
      basicAddons: getProductStatsForModal(
        products,
        "BASIC",
        "ADDON_SCREEN",
      ) || { quantity: 0, price: 0, totalPrice: 0 },
      status: item.status || item.invoiceStatus || "Unpaid",
      total: item.totalAmount || 0,
      currency: item.currency || "USD",
      code: item.code,
      invoiceUrl: item.invoiceUrl,
      isCouponApplied: item.isCouponApplied,
      coupon: item.coupon,
      couponDiscountAmount: item.couponDiscountAmount,
      couponDiscountPercentage: item.couponDiscountPercentage,
      specialDiscountAmount: item.specialDiscountAmount,
      specialDiscountPercentage: item.specialDiscountPercentage,
      taxAmount: item.taxAmount,
      taxPercentage: item.taxPercentage,
      totalAmount: item.totalAmount,
      totalCapturedAmount: item.totalCapturedAmount,
      totalDueAmount: item.totalDueAmount,
      totalReductionAmount: item.totalReductionAmount,
      totalProductAmount: item.totalProductAmount,
    };
    setSelectedInvoice(mapped);
    setOpenInvoice(true);
  };

  React.useEffect(() => {
    if (billingData && !isFetching) {
      const newResults =
        billingData.results ||
        (Array.isArray(billingData) ? billingData : [billingData]);
      if (Array.isArray(newResults)) {
        if (page === 1) {
          setAllBillingRows(newResults);
        } else {
          setAllBillingRows((prev) => {
            const existingIds = new Set(prev.map((item) => item._id));
            const uniqueNewResults = newResults.filter((item) => !existingIds.has(item._id));
            return [...prev, ...uniqueNewResults];
          });
        }

        // If results are fewer than limit, we reached the end
        if (newResults.length < 10) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } else {
        setHasMore(false);
      }
    }
  }, [billingData, page, isFetching]);

  const displayBillingRows = React.useMemo(() => {
    let upcomingRows: any[] = [];
    if (upcomingData) {
      const rawUpcoming = upcomingData.results || (upcomingData.result ? [upcomingData.result] : (Array.isArray(upcomingData) ? upcomingData : [upcomingData]));
      if (Array.isArray(rawUpcoming)) {
        upcomingRows = rawUpcoming.map(item => ({
          ...item,
          status: "UPCOMING" // Ensure status is consistent for styling
        }));
      }
    }
    return [...upcomingRows, ...allBillingRows];
  }, [allBillingRows, upcomingData]);

  React.useEffect(() => {
    if (licensesData) {
      const newResults =
        licensesData.results ||
        (Array.isArray(licensesData) ? licensesData : [licensesData]);

      if (Array.isArray(newResults)) {
        if (licensePage === 1) {
          setAllLicenses(newResults);
        } else {
          setAllLicenses((prev) => [...prev, ...newResults]);
        }

        if (newResults.length < 50) {
          setHasMoreLicenses(false);
        } else {
          setHasMoreLicenses(true);
        }
      } else {
        setHasMoreLicenses(false);
      }
    }
  }, [licensesData, licensePage]);

  const loaderRef = React.useRef<HTMLTableRowElement | null>(null);
  const licenseLoaderRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }, // Lower threshold for easier triggering
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isFetching, allBillingRows.length]); // Re-attach when list changes

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreLicenses &&
          !isFetchingLicenses
        ) {
          setLicensePage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 },
    );

    const node = licenseLoaderRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
      observer.disconnect();
    };
  }, [hasMoreLicenses, isFetchingLicenses, allLicenses.length]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB"); // Format: DD/MM/YYYY
  };

  const getProductCount = (products: any[], type: "PREMIUM" | "BASIC") => {
    return (products || [])
      .filter((p: any) => p.type === type)
      .reduce((acc: number, curr: any) => acc + (curr.quantity || 0), 0);
  };

  const { admin, isLoggedIn } = useAppSelector((state: RootState) => state.admin as any);
  const { data: profileResp } = useGetProfileQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
  });
  const profile = admin ?? profileResp?.result ?? profileResp;
  const rawRole =
    (profile as any)?.role ??
    (profile as any)?.data?.role ??
    (profile as any)?.user?.role ??
    "";
  const isSuperAdmin =
    typeof rawRole === "string" &&
    rawRole.toLowerCase().replace(/[\s_-]+/g, "") === "superadmin";

  const pageBg = themeBox ? "bg-[#0B0B0B]" : "bg-gray-50";
  const cardBg = themeBox
    ? "bg-[#1A1A1A] border-[#2A2A2A]"
    : "bg-white border-gray-200";
  const textColor = themeBox ? "text-gray-100" : "text-gray-900";
  const subTextColor = themeBox ? "text-gray-400" : "text-gray-500";

  const actionCards = [
    {
      title: "Add New Screens",
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="6"
            y="7"
            width="12"
            height="8"
            rx="1.5"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
          />
          <line
            x1="12"
            y1="15"
            x2="12"
            y2="18"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <line
            x1="9"
            y1="18"
            x2="15"
            y2="18"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
          />

          <line
            x1="12"
            y1="9"
            x2="12"
            y2="13"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <line
            x1="10"
            y1="11"
            x2="14"
            y2="11"
            stroke={themeBox ? "white" : "#3A3A3A"}
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
      ),
      path: adminRoutes.licenses,
    },
    {
      title: "Add New Organization",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 2H6C3.518 2 3 2.518 3 5V22H15V5C15 2.518 14.482 2 12 2Z"
            stroke={themeBox ? "#F3F3F6" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M18 8H15V22H21V11C21 8.518 20.482 8 18 8Z"
            stroke={themeBox ? "#F3F3F6" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M8 6H10M8 9H10M8 12H10"
            stroke="#B6CB24"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11.5 22V18C11.5 17.0572 11.5 16.5858 11.2071 16.2929C10.9142 16 10.4428 16 9.5 16H8.5C7.55719 16 7.08579 16 6.79289 16.2929C6.5 16.5858 6.5 17.0572 6.5 18V22"
            stroke={themeBox ? "#F3F3F6" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      ),
      path: adminRoutes.organisationCreate,
    },
    ...(isSuperAdmin
      ? [
        {
          title: "Add New Admin",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M15 8C15 5.23858 12.7614 3 10 3C7.23858 3 5 5.23858 5 8C5 10.7614 7.23858 13 10 13C12.7614 13 15 10.7614 15 8Z"
                stroke={themeBox ? "white" : "#3A3A3A"}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.5 21V14M14 17.5H21"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 20C3 16.134 6.13401 13 10 13C11.4872 13 12.8662 13.4638 14 14.2547"
                stroke={themeBox ? "white" : "#3A3A3A"}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
          path: adminRoutes.adminCreate,
        },
      ]
      : [
        {
          title: "View Devices",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <rect
                x="2"
                y="3"
                width="20"
                height="14"
                rx="2"
                stroke={themeBox ? "white" : "#3A3A3A"}
                strokeWidth="1.5"
              />
              <path
                d="M8 21H16"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M12 17V21"
                stroke={themeBox ? "white" : "#3A3A3A"}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M7 10H17M7 13H13"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          ),
          path: adminRoutes.deviceList,
        },
      ]),
    {
      title: "Add New Gym",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M18 3V8M6 3V8"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M20.5 4V5.5M20.5 5.5V7M20.5 5.5H22M3.5 4V5.5M3.5 5.5V7M3.5 5.5H2"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18 5.5H6"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7.27653 19H16.7235C17.961 19 18.5797 19 18.8356 18.6974C19.4163 18.0107 18.3038 17.1031 17.8979 16.6456C17.4405 16.1302 17.1059 16 16.4299 16H7.57013C6.89408 16 6.55953 16.1302 6.10214 16.6456C5.69617 17.1031 4.58375 18.0107 5.16444 18.6974C5.42026 19 6.03902 19 7.27653 19Z"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 8V16M15 8V16"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 19V21M8 19V21"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M20.4987 9.83337V15.1667M23.1654 12.5H17.832"
            stroke="#B6CB24"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      path: adminRoutes.gymCreate,
    },
    {
      title: "Add New Coach",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M15 8C15 5.23858 12.7614 3 10 3C7.23858 3 5 5.23858 5 8C5 10.7614 7.23858 13 10 13C12.7614 13 15 10.7614 15 8Z"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.5 21V14M14 17.5H21"
            stroke="#C6D92C"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M3 20C3 16.134 6.13401 13 10 13C11.4872 13 12.8662 13.4638 14 14.2547"
            stroke={themeBox ? "white" : "#3A3A3A"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      path: adminRoutes.staffCreate,
    },
  ];

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Scoped scrollbar styles */}
      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;                /* Firefox */
          scrollbar-color: #00000038 transparent;    /* Firefox */
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #000;               /* Black thumb */
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #000;
        }
      `}</style>

      {/* Header */}
      <div className="mb-2">
        <h1 className={`text-2xl font-semibold ${textColor}`}>Dashboard</h1>
      </div>

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {actionCards.map((card, idx) => (
          <div
            key={idx}
            onClick={() => navigate(card.path)}
            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer shadow-lg border relative overflow-hidden group transition-all duration-300 ${themeBox
              ? "bg-gradient-to-br from-[#1E1E1E] to-[#0F0F0F] border-[#2A2A2A] hover:border-[#3A3A3A]"
              : "bg-white border-gray-100 hover:border-gray-200"
              }`}
          >
            {/* Glossy Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

            {/* Icon */}
            {themeBox ? (
              // Dark theme: keep existing style
              <div className="w-11 h-11 flex items-center justify-center rounded-full border flex-shrink-0 bg-black border-[#2A2A2A]">
                {card.icon}
              </div>
            ) : (
              // Light theme: double-circle style
              <div className="flex items-center justify-center flex-shrink-0">
                <div className="relative flex items-center justify-center">
                  {/* Outer ring */}
                  <div className="w-14 h-14 rounded-full border border-[#D4E89E]" />
                  {/* Inner filled circle */}
                  <div className="absolute w-10 h-10 rounded-full bg-[#F2F8CC] border border-[#D4E89E] flex items-center justify-center">
                    {card.icon}
                  </div>
                </div>
              </div>
            )}

            <span
              className={`text-[13px] font-semibold leading-tight ${textColor}`}
            >
              {card.title}
            </span>
          </div>
        ))}
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        {/* Total Licenses Card */}
        <div
          className={`lg:col-span-5 rounded-xl border ${cardBg} p-3 h-full shadow-sm`}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className={`text-xs font-medium ${subTextColor}`}>
              Total Licenses
            </h3>
            <button
              onClick={() => navigate(adminRoutes.licenses)}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md border border-[#B6CB24] text-[#B6CB24] text-[10px] font-medium hover:bg-[#B6CB24]/10 transition-colors"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
              Manage
            </button>
          </div>

          <div className="space-y-3">
            {/* Assigned License Section */}
            <div>
              {/* Header with background */}
              <div
                className={`flex items-center justify-between px-2.5 py-2 rounded-t-lg ${themeBox ? "bg-[#292929]" : "bg-gray-100"}`}
              >
                <span className={`text-xs font-medium ${textColor}`}>
                  Assigned License
                </span>
                <span className={`text-sm font-semibold ${textColor}`}>
                  {totalAssignedText}
                </span>
              </div>
              {/* Content area */}
              <div
                className={`rounded-b-lg p-2.5 space-y-2 ${themeBox ? "bg-[#1F1F1F]" : "bg-gray-50"}`}
              >
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full  flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12.75"
                          cy="12.75"
                          r="10.75"
                          fill="url(#paint0_radial_3497_6479)"
                        />
                        <g filter="url(#filter0_n_3497_6479)">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                            fill="url(#paint1_linear_3497_6479)"
                          />
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                            fill="#C6D92C"
                            fill-opacity="0.1"
                          />
                        </g>
                        <circle
                          cx="12.75"
                          cy="12.75"
                          r="10.25"
                          stroke="url(#paint2_linear_3497_6479)"
                          stroke-opacity="0.88"
                        />
                        <path
                          d="M11.2939 7.81641C11.6993 7.06107 12.8114 7.06112 13.2168 7.81641L14.4531 10.1191L14.5117 10.2295L14.6357 10.249L17.3369 10.6572C18.1918 10.7865 18.5864 11.8269 17.9287 12.4531L16.0176 14.2734L15.9219 14.3652L15.9434 14.4951L16.3633 17.0254C16.4394 17.4839 16.238 17.8433 15.9238 18.0518C15.6023 18.2649 15.1713 18.3136 14.8096 18.1387L12.3643 16.9561L12.2559 16.9033L12.1465 16.9561L9.70117 18.1387C9.33945 18.3135 8.90938 18.2648 8.58789 18.0518C8.27342 17.8433 8.07137 17.4841 8.14746 17.0254L8.56738 14.4951L8.58887 14.3652L8.49316 14.2734L6.58203 12.4531C5.92445 11.8269 6.31904 10.7865 7.17383 10.6572L9.875 10.249L9.99902 10.2295L10.0586 10.1191L11.2939 7.81641Z"
                          stroke="url(#paint3_linear_3497_6479)"
                          stroke-opacity="0.88"
                          stroke-width="0.5"
                        />
                        <defs>
                          <filter
                            id="filter0_n_3497_6479"
                            x="2"
                            y="2"
                            width="21.5"
                            height="21.5"
                            filterUnits="userSpaceOnUse"
                            color-interpolation-filters="sRGB"
                          >
                            <feFlood
                              flood-opacity="0"
                              result="BackgroundImageFix"
                            />
                            <feBlend
                              mode="normal"
                              in="SourceGraphic"
                              in2="BackgroundImageFix"
                              result="shape"
                            />
                            <feTurbulence
                              type="fractalNoise"
                              baseFrequency="2 2"
                              stitchTiles="stitch"
                              numOctaves="3"
                              result="noise"
                              seed="2744"
                            />
                            <feColorMatrix
                              in="noise"
                              type="luminanceToAlpha"
                              result="alphaNoise"
                            />
                            <feComponentTransfer
                              in="alphaNoise"
                              result="coloredNoise1"
                            >
                              <feFuncA
                                type="discrete"
                                tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                              />
                            </feComponentTransfer>
                            <feComposite
                              operator="in"
                              in2="shape"
                              in="coloredNoise1"
                              result="noise1Clipped"
                            />
                            <feFlood
                              flood-color="rgba(0, 0, 0, 0.25)"
                              result="color1Flood"
                            />
                            <feComposite
                              operator="in"
                              in2="noise1Clipped"
                              in="color1Flood"
                              result="color1"
                            />
                            <feMerge result="effect1_noise_3497_6479">
                              <feMergeNode in="shape" />
                              <feMergeNode in="color1" />
                            </feMerge>
                          </filter>
                          <radialGradient
                            id="paint0_radial_3497_6479"
                            cx="0"
                            cy="0"
                            r="1"
                            gradientUnits="userSpaceOnUse"
                            gradientTransform="translate(10.5 10) rotate(54.7824) scale(10.4043)"
                          >
                            <stop stop-color="#D1E156" />
                            <stop offset="0.552885" stop-color="#C6D92C" />
                            <stop offset="1" stop-color="#7A8618" />
                          </radialGradient>
                          <linearGradient
                            id="paint1_linear_3497_6479"
                            x1="12.75"
                            y1="3"
                            x2="12.75"
                            y2="22.5"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#2E2E2E" />
                            <stop offset="1" stop-color="#666666" />
                          </linearGradient>
                          <linearGradient
                            id="paint2_linear_3497_6479"
                            x1="2"
                            y1="12.75"
                            x2="23.5"
                            y2="12.75"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#C6D92C" />
                            <stop offset="0.533654" stop-color="#EDF5C0" />
                            <stop offset="1" stop-color="#C6D92C" />
                          </linearGradient>
                          <linearGradient
                            id="paint3_linear_3497_6479"
                            x1="6"
                            y1="12.7476"
                            x2="18.5109"
                            y2="12.7476"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#C6D92C" />
                            <stop offset="0.533654" stop-color="#EDF5C0" />
                            <stop offset="1" stop-color="#C6D92C" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                    <span className={subTextColor}>Premium</span>
                  </div>
                  <span className={textColor}>{statAssignedPremium}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <g filter="url(#filter0_n_3497_6495)">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2Z"
                            fill="url(#paint0_linear_3497_6495)"
                          />
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2Z"
                            fill="#C6D92C"
                            fill-opacity="0.1"
                          />
                        </g>
                        <circle
                          cx="12.75"
                          cy="12.75"
                          r="10.25"
                          stroke="url(#paint1_linear_3497_6495)"
                          stroke-opacity="0.88"
                        />
                        <defs>
                          <filter
                            id="filter0_n_3497_6495"
                            x="2"
                            y="2"
                            width="21.5"
                            height="21.5"
                            filterUnits="userSpaceOnUse"
                            color-interpolation-filters="sRGB"
                          >
                            <feFlood
                              flood-opacity="0"
                              result="BackgroundImageFix"
                            />
                            <feBlend
                              mode="normal"
                              in="SourceGraphic"
                              in2="BackgroundImageFix"
                              result="shape"
                            />
                            <feTurbulence
                              type="fractalNoise"
                              baseFrequency="2 2"
                              stitchTiles="stitch"
                              numOctaves="3"
                              result="noise"
                              seed="2744"
                            />
                            <feColorMatrix
                              in="noise"
                              type="luminanceToAlpha"
                              result="alphaNoise"
                            />
                            <feComponentTransfer
                              in="alphaNoise"
                              result="coloredNoise1"
                            >
                              <feFuncA
                                type="discrete"
                                tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                              />
                            </feComponentTransfer>
                            <feComposite
                              operator="in"
                              in2="shape"
                              in="coloredNoise1"
                              result="noise1Clipped"
                            />
                            <feFlood
                              flood-color="rgba(0, 0, 0, 0.25)"
                              result="color1Flood"
                            />
                            <feComposite
                              operator="in"
                              in2="noise1Clipped"
                              in="color1Flood"
                              result="color1"
                            />
                            <feMerge result="effect1_noise_3497_6495">
                              <feMergeNode in="shape" />
                              <feMergeNode in="color1" />
                            </feMerge>
                          </filter>
                          <linearGradient
                            id="paint0_linear_3497_6495"
                            x1="12.75"
                            y1="3"
                            x2="12.75"
                            y2="22.5"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#2E2E2E" />
                            <stop offset="1" stop-color="#666666" />
                          </linearGradient>
                          <linearGradient
                            id="paint1_linear_3497_6495"
                            x1="2"
                            y1="12.75"
                            x2="23.5"
                            y2="12.75"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#C6D92C" />
                            <stop offset="0.533654" stop-color="#EDF5C0" />
                            <stop offset="1" stop-color="#C6D92C" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>{" "}
                    <span className={subTextColor}>Basic</span>
                  </div>
                  <span className={textColor}>{statAssignedBasic}</span>
                </div>
              </div>
            </div>

            {/* Available License Section */}
            <div>
              {/* Header with background */}
              <div
                className={`flex items-center justify-between px-2.5 py-2 rounded-t-lg ${themeBox ? "bg-[#292929]" : "bg-gray-100"}`}
              >
                <span className={`text-xs font-medium ${textColor}`}>
                  Available License{" "}
                  <span
                    className={`text-[10px] ${themeBox ? " text-[#EDF5C0]" : "text-[#86931A]"} px-1 rounded`}
                  >
                    (Unassigned to Gyms)
                  </span>
                </span>
                <span className={`text-sm font-semibold ${textColor}`}>
                  {totalUnassignedText}
                </span>
              </div>
              {/* Content area */}
              <div
                className={`rounded-b-lg p-2.5 space-y-2 ${themeBox ? "bg-[#1F1F1F]" : "bg-gray-50"}`}
              >
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full  flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12.75"
                          cy="12.75"
                          r="10.75"
                          fill="url(#paint0_radial_3497_6479)"
                        />
                        <g filter="url(#filter0_n_3497_6479)">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                            fill="url(#paint1_linear_3497_6479)"
                          />
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                            fill="#C6D92C"
                            fill-opacity="0.1"
                          />
                        </g>
                        <circle
                          cx="12.75"
                          cy="12.75"
                          r="10.25"
                          stroke="url(#paint2_linear_3497_6479)"
                          stroke-opacity="0.88"
                        />
                        <path
                          d="M11.2939 7.81641C11.6993 7.06107 12.8114 7.06112 13.2168 7.81641L14.4531 10.1191L14.5117 10.2295L14.6357 10.249L17.3369 10.6572C18.1918 10.7865 18.5864 11.8269 17.9287 12.4531L16.0176 14.2734L15.9219 14.3652L15.9434 14.4951L16.3633 17.0254C16.4394 17.4839 16.238 17.8433 15.9238 18.0518C15.6023 18.2649 15.1713 18.3136 14.8096 18.1387L12.3643 16.9561L12.2559 16.9033L12.1465 16.9561L9.70117 18.1387C9.33945 18.3135 8.90938 18.2648 8.58789 18.0518C8.27342 17.8433 8.07137 17.4841 8.14746 17.0254L8.56738 14.4951L8.58887 14.3652L8.49316 14.2734L6.58203 12.4531C5.92445 11.8269 6.31904 10.7865 7.17383 10.6572L9.875 10.249L9.99902 10.2295L10.0586 10.1191L11.2939 7.81641Z"
                          stroke="url(#paint3_linear_3497_6479)"
                          stroke-opacity="0.88"
                          stroke-width="0.5"
                        />
                        <defs>
                          <filter
                            id="filter0_n_3497_6479"
                            x="2"
                            y="2"
                            width="21.5"
                            height="21.5"
                            filterUnits="userSpaceOnUse"
                            color-interpolation-filters="sRGB"
                          >
                            <feFlood
                              flood-opacity="0"
                              result="BackgroundImageFix"
                            />
                            <feBlend
                              mode="normal"
                              in="SourceGraphic"
                              in2="BackgroundImageFix"
                              result="shape"
                            />
                            <feTurbulence
                              type="fractalNoise"
                              baseFrequency="2 2"
                              stitchTiles="stitch"
                              numOctaves="3"
                              result="noise"
                              seed="2744"
                            />
                            <feColorMatrix
                              in="noise"
                              type="luminanceToAlpha"
                              result="alphaNoise"
                            />
                            <feComponentTransfer
                              in="alphaNoise"
                              result="coloredNoise1"
                            >
                              <feFuncA
                                type="discrete"
                                tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                              />
                            </feComponentTransfer>
                            <feComposite
                              operator="in"
                              in2="shape"
                              in="coloredNoise1"
                              result="noise1Clipped"
                            />
                            <feFlood
                              flood-color="rgba(0, 0, 0, 0.25)"
                              result="color1Flood"
                            />
                            <feComposite
                              operator="in"
                              in2="noise1Clipped"
                              in="color1Flood"
                              result="color1"
                            />
                            <feMerge result="effect1_noise_3497_6479">
                              <feMergeNode in="shape" />
                              <feMergeNode in="color1" />
                            </feMerge>
                          </filter>
                          <radialGradient
                            id="paint0_radial_3497_6479"
                            cx="0"
                            cy="0"
                            r="1"
                            gradientUnits="userSpaceOnUse"
                            gradientTransform="translate(10.5 10) rotate(54.7824) scale(10.4043)"
                          >
                            <stop stop-color="#D1E156" />
                            <stop offset="0.552885" stop-color="#C6D92C" />
                            <stop offset="1" stop-color="#7A8618" />
                          </radialGradient>
                          <linearGradient
                            id="paint1_linear_3497_6479"
                            x1="12.75"
                            y1="3"
                            x2="12.75"
                            y2="22.5"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#2E2E2E" />
                            <stop offset="1" stop-color="#666666" />
                          </linearGradient>
                          <linearGradient
                            id="paint2_linear_3497_6479"
                            x1="2"
                            y1="12.75"
                            x2="23.5"
                            y2="12.75"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#C6D92C" />
                            <stop offset="0.533654" stop-color="#EDF5C0" />
                            <stop offset="1" stop-color="#C6D92C" />
                          </linearGradient>
                          <linearGradient
                            id="paint3_linear_3497_6479"
                            x1="6"
                            y1="12.7476"
                            x2="18.5109"
                            y2="12.7476"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#C6D92C" />
                            <stop offset="0.533654" stop-color="#EDF5C0" />
                            <stop offset="1" stop-color="#C6D92C" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                    <span className={subTextColor}>Premium</span>
                  </div>
                  <span className={textColor}>{statUnassignedPremium}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full  flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <g filter="url(#filter0_n_3497_6495)">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2Z"
                            fill="url(#paint0_linear_3497_6495)"
                          />
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2Z"
                            fill="#C6D92C"
                            fill-opacity="0.1"
                          />
                        </g>
                        <circle
                          cx="12.75"
                          cy="12.75"
                          r="10.25"
                          stroke="url(#paint1_linear_3497_6495)"
                          stroke-opacity="0.88"
                        />
                        <defs>
                          <filter
                            id="filter0_n_3497_6495"
                            x="2"
                            y="2"
                            width="21.5"
                            height="21.5"
                            filterUnits="userSpaceOnUse"
                            color-interpolation-filters="sRGB"
                          >
                            <feFlood
                              flood-opacity="0"
                              result="BackgroundImageFix"
                            />
                            <feBlend
                              mode="normal"
                              in="SourceGraphic"
                              in2="BackgroundImageFix"
                              result="shape"
                            />
                            <feTurbulence
                              type="fractalNoise"
                              baseFrequency="2 2"
                              stitchTiles="stitch"
                              numOctaves="3"
                              result="noise"
                              seed="2744"
                            />
                            <feColorMatrix
                              in="noise"
                              type="luminanceToAlpha"
                              result="alphaNoise"
                            />
                            <feComponentTransfer
                              in="alphaNoise"
                              result="coloredNoise1"
                            >
                              <feFuncA
                                type="discrete"
                                tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                              />
                            </feComponentTransfer>
                            <feComposite
                              operator="in"
                              in2="shape"
                              in="coloredNoise1"
                              result="noise1Clipped"
                            />
                            <feFlood
                              flood-color="rgba(0, 0, 0, 0.25)"
                              result="color1Flood"
                            />
                            <feComposite
                              operator="in"
                              in2="noise1Clipped"
                              in="color1Flood"
                              result="color1"
                            />
                            <feMerge result="effect1_noise_3497_6495">
                              <feMergeNode in="shape" />
                              <feMergeNode in="color1" />
                            </feMerge>
                          </filter>
                          <linearGradient
                            id="paint0_linear_3497_6495"
                            x1="12.75"
                            y1="3"
                            x2="12.75"
                            y2="22.5"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#2E2E2E" />
                            <stop offset="1" stop-color="#666666" />
                          </linearGradient>
                          <linearGradient
                            id="paint1_linear_3497_6495"
                            x1="2"
                            y1="12.75"
                            x2="23.5"
                            y2="12.75"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#C6D92C" />
                            <stop offset="0.533654" stop-color="#EDF5C0" />
                            <stop offset="1" stop-color="#C6D92C" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>{" "}
                    <span className={subTextColor}>Basic</span>
                  </div>
                  <span className={textColor}>{statUnassignedBasic}</span>
                </div>
              </div>
            </div>
          </div>
          {/* Infinite scroll trigger for licenses */}
          <div ref={licenseLoaderRef} style={{ height: 1 }} />
        </div>

        {/* Small Progress Cards */}
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              label: "Total Screens",
              premium: statTotalScreensPremium,
              basic: statTotalScreensBasic,
            },
            {
              label: "Active Screens",
              premium: statActiveScreensPremium,
              basic: statActiveScreensBasic,
            },
            {
              label: "Idle Screens",
              premium: statIdleScreensPremium,
              basic: statIdleScreensBasic,
            },
          ].map((stat, i) => (
            <div
              key={i}
              className={`rounded-xl border ${cardBg} p-4 h-full shadow-sm`}
            >
              <div className="flex items-center justify-between  mb-4 border-b border-gray-700 pb-2">
                <span className={`text-sm font-medium ${subTextColor}`}>
                  {stat.label}
                </span>
                <div
                  className={`w-6 h-6 rounded flex items-center justify-center `}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M2.5 12C2.5 7.52166 2.5 5.28249 3.89124 3.89124C5.28249 2.5 7.52166 2.5 12 2.5C16.4783 2.5 18.7175 2.5 20.1088 3.89124C21.5 5.28249 21.5 7.52166 21.5 12C21.5 16.4783 21.5 18.7175 20.1088 20.1088C18.7175 21.5 16.4783 21.5 12 21.5C7.52166 21.5 5.28249 21.5 3.89124 20.1088C2.5 18.7175 2.5 16.4783 2.5 12Z"
                      stroke="#F3F3F6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 16V11.5"
                      stroke="#C6D92C"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 8.01171V8.00171"
                      stroke="#C6D92C"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <div
                className={`aspect-[4/3] rounded-xl relative flex items-center justify-center overflow-hidden`}
              >
                {/* TV Display Graphic */}
                <div
                  className={`absolute inset-4 rounded-lg flex flex-col items-center justify-center border transition-colors ${themeBox
                    ? "bg-black/40 border-[#333]"
                    : "bg-white/60 border-gray-200 shadow-inner"
                    }`}
                >
                  <div className="flex items-center justify-center gap-8 mb-2">
                    {/* Premium Circle */}
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className={`w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${themeBox ? "border-[#B6CB24]" : "border-[#B6CB24]"
                          }`}
                      >
                        <span className="text-lg font-bold text-[#B6CB24]">
                          {stat.premium}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-medium ${themeBox ? "text-gray-400" : "text-gray-500"
                          }`}
                      >
                        Premium
                      </span>
                    </div>

                    {/* Basic Circle */}
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className={`w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${themeBox ? "border-gray-700" : "border-gray-200"
                          }`}
                      >
                        <span
                          className={`text-lg font-bold ${themeBox ? "text-gray-200" : "text-gray-700"
                            }`}
                        >
                          {stat.basic}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-medium ${themeBox ? "text-gray-400" : "text-gray-500"
                          }`}
                      >
                        Basic
                      </span>
                    </div>
                  </div>
                </div>
                {/* TV Stand */}
                <div
                  className={`absolute bottom-2 left-1/2 -translate-x-1/2 w-8 h-1.5`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="4"
                    viewBox="0 0 21 4"
                    fill="none"
                  >
                    <path
                      d="M17.3835 3.30959H3.30859C1.48105 3.30959 0 1.82809 0 0H20.6909C20.6909 1.82809 19.2098 3.30959 17.3823 3.30959H17.3835Z"
                      fill="url(#paint0_linear_3497_6454)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_3497_6454"
                        x1="10.3461"
                        y1="3.30959"
                        x2="10.3461"
                        y2="0.00125138"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#5C5C5C" />
                        <stop offset="1" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Billing Details Section */}
      <div
        className={`rounded-xl border ${cardBg} shadow-sm overflow-hidden flex flex-col`}
      >
        <div className="p-6 border-b border-[#2A2A2A]">
          <h2 className={`text-base font-semibold ${textColor}`}>
            Billing Details
          </h2>
        </div>
        <div className="overflow-x-auto">
          <div className="max-h-[450px] overflow-y-auto custom-scrollbar">
            <table className="min-w-full w-full text-left border-collapse">
              <thead>
                <tr
                  className={`text-xs uppercase tracking-wider ${subTextColor} border-b border-[#232323]`}
                >
                  <th
                    className={`sticky top-0 z-20 px-6 py-4 font-semibold ${themeBox ? "bg-[#1A1A1A]" : "bg-white"}`}
                  >
                    Billing Date
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-4 font-semibold ${themeBox ? "bg-[#1A1A1A]" : "bg-white"}`}
                  >
                    End Date
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-4 font-semibold ${themeBox ? "bg-[#1A1A1A]" : "bg-white"}`}
                  >
                    Description
                  </th>
                  {/* <th className="px-6 py-4 font-semibold">Premium</th>
                <th className="px-6 py-4 font-semibold">Basic</th> */}
                  <th
                    className={`sticky top-0 z-20 px-6 py-4 font-semibold ${themeBox ? "bg-[#1A1A1A]" : "bg-white"}`}
                  >
                    Status
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-4 font-semibold ${themeBox ? "bg-[#1A1A1A]" : "bg-white"}`}
                  >
                    Total
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-4 font-semibold ${themeBox ? "bg-[#1A1A1A]" : "bg-white"}`}
                  ></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#232323]">
                {displayBillingRows.length > 0 ? (
                  displayBillingRows.map((invoice: any, i: number) => {
                    const items = invoice?.products || [];
                    return (
                      <tr
                        key={invoice?._id || i}
                        className={`text-sm ${themeBox ? "hover:bg-[#222222]" : "hover:bg-gray-50"} transition-colors`}
                      >
                        <td className={`px-6 py-4 ${textColor}`}>
                          {(() => {
                            const baseDate = subscriptionSummary?.results?.[0]?.nextBillingAt;
                            return baseDate ? new Date(baseDate).toLocaleDateString("en-GB") : "--";
                          })()}
                        </td>
                        <td className={`px-6 py-4 ${textColor}`}>
                          {(() => {
                            const baseDate = subscriptionSummary?.results?.[0]?.nextBillingAt;
                            if (baseDate) {
                              const d = new Date(baseDate);
                              d.setDate(d.getDate() + 28);
                              return d.toLocaleDateString("en-GB");
                            }
                            return "--";
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M20.9999 10.5V10C20.9999 6.22876 20.9999 4.34315 19.8284 3.17157C18.6568 2 16.7712 2 12.9999 2H12C8.22883 2 6.34323 2 5.17166 3.17156C4.00009 4.34312 4.00007 6.22872 4.00004 9.99993L4 14.5C3.99997 17.7874 3.99996 19.4312 4.90788 20.5375C5.07412 20.7401 5.25986 20.9258 5.46242 21.0921C6.56877 22 8.21249 22 11.4999 22"
                                stroke="#F3F3F6"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M7.5 7H16.5"
                                stroke="#F3F3F6"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M7.5 12H13.5"
                                stroke="#F3F3F6"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M20.5 20V17C20.5 15.5706 19.1569 14 17.5 14C15.8431 14 14.5 15.5706 14.5 17V20.5C14.5 21.3284 15.1716 22 16 22C16.8284 22 17.5 21.3284 17.5 20.5V17"
                                stroke="#C6D92C"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className={subTextColor}>
                              {invoice?.products?.[0]?.name ||
                                "Subscription Plan"}
                            </span>
                          </div>
                        </td>
                        {/* <td className={`px-6 py-4 ${textColor}`}>
                        {getProductCount(items, "PREMIUM")}
                      </td>
                      <td className={`px-6 py-4 ${textColor}`}>
                        {getProductCount(items, "BASIC")}
                      </td> */}
                        <td className={`px-6 py-4 ${textColor}`}>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${String(invoice?.status).toUpperCase() === "PAID"
                              ? "bg-green-100 text-green-700"
                              : String(invoice?.status).toUpperCase() === "UPCOMING"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-yellow-100 text-yellow-700"
                              }`}
                          >
                            {String(invoice?.status).charAt(0).toUpperCase() + String(invoice?.status).slice(1).toLowerCase()}
                          </span>
                        </td>
                        <td className={`px-6 py-4 ${textColor}`}>
                          {invoice?.currency === "USD"
                            ? "$"
                            : invoice?.currency === "AED"
                              ? "د.إ"
                              : invoice?.currency === "INR"
                                ? "₹"
                                : invoice?.currency}{" "}
                          {invoice?.totalAmount ||
                            invoice?.totalCapturedAmount ||
                            0}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleViewInvoice(invoice)}
                            className="inline-flex items-center gap-1 px-4 py-1 rounded-full border border-gray-700 text-xs font-medium text-gray-300 hover:bg-gray-800 transition-colors"
                          >
                            View
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M9.00005 6L15 12L9 18"
                                stroke="#C6D92C"
                                strokeWidth="1.5"
                                strokeMiterlimit="16"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className={`px-6 py-8 text-center ${subTextColor}`}
                    >
                      {isFetching && displayBillingRows.length === 0
                        ? "Loading..."
                        : "No billing records found."}
                    </td>
                  </tr>
                )}
                {isFetching && displayBillingRows.length > 0 && (
                  <tr>
                    <td colSpan={8} className="py-4 text-center">
                      <span className={subTextColor}>Loading more...</span>
                    </td>
                  </tr>
                )}
                {/* Infinite Scroll Loader Trigger */}
                <tr ref={loaderRef}>
                  <td colSpan={8} style={{ height: "1px" }}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      {selectedInvoice && (
        <InvoiceModal
          open={openInvoice}
          data={selectedInvoice}
          onClose={() => setOpenInvoice(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
